import Loading from '@leaflink/stash/Loading.vue';
import { storeToRefs } from 'pinia';
import { nextTick } from 'vue';
import { createRouter as createVueRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

import { useErrorPage } from '@/composables/useErrorPage';
import { LAYOUTS, ROUTE_NAMES, SPLIT_NAMES } from '@/constants';
import Company from '@/pages/Company/index.vue';
import Oauth2Callback from '@/pages/Oauth2Callback/Oauth2Callback.vue';
import { useAuthStore } from '@/piniaStores/auth/auth.store';
import { useCompanyStore } from '@/piniaStores/company/company.store';
import { useUserStore } from '@/piniaStores/user/user.store';
import i18n, { loadTranslations } from '@/plugins/i18n';
import { getUtmParams } from '@/services/api/utm';
import { notifyPendingSystemNofications } from '@/services/systemNotifications/systemNotificationsService';
import { trackPage } from '@/track';
import { decodeSegmentTrackPageDataFromQueryParams } from '@/utils/segmentTrackPageData';

import adminRoutes from './admin';
import advertisingRoutes from './advertising';
import cartRoutes from './cart';
import companyRoutes from './company';
import crmRoutes from './CRM';
import deliveryProviderRoutes from './deliveryProvider';
import enrollmentRoutes from './enrollment';
import fulfillmentRoutes from './fulfillment';
import {
  appConfigGuard,
  authenticationCallbackGuard,
  authenticationGuard,
  authenticationRedirectGuard,
  delinquencyGuard,
  fetchCommonData,
  licenseGuard,
  metaPermissionsGuard,
  serverRenderedRouteGuard,
} from './guards';
import inventoryRoutes from './inventory';
import logisticsRoutes from './logistics';
import logisticsAdminRoutes from './logisticsAdmin';
import orderRoutes from './orders';
import paymentsRoutes from './payments';
import pricingRoutes from './pricing';
import registrationRoutes from './registration';
import replenishmentRoutes from './replenishment';
import reportsRoutes from './reports';
import seedToSaleRoutes from './seedToSale';
import settingsRoutes from './settings';
import shipAssistRoutes from './shipAssist';
import shopRoutes from './shop';
import userRoutes from './user';
import { isVueRoute, toNonVueRoute } from './utils';
import warehouseRoutes from './warehouse';

const JoinToday = () => import('@/pages/JoinToday/JoinToday.vue');
const ComparePlans = () => import('@/pages/FeatureEnrollment/ComparePlans/ComparePlans.vue');

export const routes: RouteRecordRaw[] = [
  {
    // The reverse proxy in CloudFront (or Vite, locally) will forward this route to LeafLink/leaflink, which will redirect either to login or to the dashboard
    name: ROUTE_NAMES.ROOT,
    path: '/',
    component: Loading,
  },
  {
    /**
     * This route is registered in the Auth0 service as the redirect_uri. It
     * should not be used for any other purpose. The Oauth2Callback component
     * exists to handle the redirect from Auth0 when the token has expired or
     * is otherwise invalidated.
     */
    path: '/oauth2-callback/',
    beforeEnter: [authenticationCallbackGuard, fetchCommonData],
    component: Oauth2Callback,
    name: ROUTE_NAMES.OAUTH2_CALLBACK,
    meta: {
      layout: LAYOUTS.FULL,
    },
  },
  {
    path: '/c/:company',
    beforeEnter: [authenticationGuard, fetchCommonData],
    component: Company,
    props: (route) => ({ company: route.params.company }),
    children: [
      ...companyRoutes,
      ...crmRoutes,
      ...deliveryProviderRoutes,
      ...enrollmentRoutes,
      ...reportsRoutes,
      ...orderRoutes,
      ...fulfillmentRoutes,
      ...inventoryRoutes,
      ...logisticsRoutes,
      ...seedToSaleRoutes,
      ...settingsRoutes,
      ...shopRoutes.companyRoutes,
      ...shipAssistRoutes,
      ...pricingRoutes,
      ...warehouseRoutes,
      toNonVueRoute({ path: 'accounting/account-mappings/:view?', name: ROUTE_NAMES.ACCOUNT_MAPPINGS }),
    ],
  },
  toNonVueRoute({ path: '/403/', name: ROUTE_NAMES.ERROR_403 }),
  toNonVueRoute({ path: '/404/', name: ROUTE_NAMES.ERROR_404 }),
  toNonVueRoute({ path: '/accounts/logout/', name: ROUTE_NAMES.LOGOUT }),
  {
    path: '/dashboard/',
    name: ROUTE_NAMES.DASHBOARD,
    redirect: { path: '/' },
  },
  {
    path: '/join-today-payments/',
    name: ROUTE_NAMES.JOIN_TODAY_PAYMENTS,
    beforeEnter: [authenticationGuard, fetchCommonData],
    component: JoinToday,
    meta: {
      permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)] }],
    },
  },
  {
    path: '/compare-plans/',
    name: ROUTE_NAMES.COMPARE_PLANS,
    component: ComparePlans,
    // fetchCommonData is necessary for segment
    beforeEnter: [fetchCommonData, () => loadTranslations('enrollment')],
    meta: {
      layout: LAYOUTS.FULL,
      permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)] }],
      title: i18n.t('enrollment.landingPage.title'),
    },
  },
  toNonVueRoute({ path: '/leaflink/features/', name: ROUTE_NAMES.LEAFLINK_FEATURES }),
  toNonVueRoute({ path: '/ll/', name: ROUTE_NAMES.ADMIN_TOOLS }),
  toNonVueRoute({ path: '/payments/', name: ROUTE_NAMES.PAYMENTS }),
  toNonVueRoute({ path: '/payments/settings/accounts/', name: ROUTE_NAMES.PAYMENTS_ACCOUNT_SETTINGS }),
  toNonVueRoute({ path: '/payments/settings/preferences/', name: ROUTE_NAMES.PAYMENTS_SETTINGS_PREFERENCES }),
  toNonVueRoute({ path: '/deliveries/', name: ROUTE_NAMES.PROOF_OF_DELIVERY }),
  ...adminRoutes,
  ...advertisingRoutes,
  ...logisticsAdminRoutes,
  ...shopRoutes.baseRoutes,
  ...cartRoutes,
  ...replenishmentRoutes,
  ...registrationRoutes,
  ...paymentsRoutes,
  ...userRoutes,
];

export function createRouter() {
  const router = createVueRouter({
    history: createWebHistory(),
    routes,
  });

  // Navigation Lifecycle/Resolution Flow: https://router.vuejs.org/guide/advanced/navigation-guards.html#The-Full-Navigation-Resolution-Flow
  router.beforeEach(() => (useErrorPage().settings = undefined));
  router.beforeEach(appConfigGuard);
  router.beforeEach(authenticationRedirectGuard);
  router.beforeEach(serverRenderedRouteGuard);

  // The following guards depend on user and company data (but fail gracefully without it), which is why beforeResolve is used so that user and company is already loaded by beforeEach/beforeEnter.
  router.beforeResolve(metaPermissionsGuard);
  router.beforeResolve(delinquencyGuard);
  router.beforeResolve(licenseGuard);

  router.afterEach((to, from) => {
    nextTick(async () => {
      const { segmentPageName, title } = to.meta;
      const { activeCompany, activeCompanyId } = storeToRefs(useCompanyStore());
      const { getIsSplitEnabled, user } = storeToRefs(useUserStore());
      let newTitle = typeof title === 'function' && activeCompany.value ? title(activeCompany.value) : title;

      if (typeof newTitle === 'string') {
        newTitle = /^\w{2,}\.\w{2,}/.test(newTitle) ? i18n.t(newTitle) : newTitle;
        document.title = `LeafLink - ${newTitle}`;
      }

      /**
       * some pages require a specific page name for the event to allow the data team to
       * better relate page and ad data.  since some page titles do not make sense out of
       * the ui context, e.g. Brand Menu having an page title of 'Shop Brands Details', we
       * may assign a Segment-specific page name to the route.  as this is optional, we may
       * fall back to the document.title, stripped of the 'LeafLink -' prefix.
       */
      const pageEventName = segmentPageName || document.title.split(' - ')[1];

      if (from.fullPath !== to.fullPath) {
        if (typeof window.surf === 'function') {
          // Surfside requires that we call their page event on every route change
          window.surf('trackPageView:digital');
        }

        if (
          getIsSplitEnabled.value(SPLIT_NAMES.SSR_IFRAME) ||
          from.path === '/' ||
          (isVueRoute(from) && isVueRoute(to))
        ) {
          /**
           * Some pages sends additional metadata to Segment, such as the algolia query ids.
           * This metadata is encoded and sent via query params.  We decode this metadata here and send it to Segment every route change.
           * It's important to note that not every page sends this metadata, so we need to check if the metadata exists before sending it.
           * The underlying function will make sure to only send the metadata that is relevant to the page.
           */
          const pageMetadata = decodeSegmentTrackPageDataFromQueryParams({ query: to.query });

          /**
           * UTM params are query params included on ad links, and can be anything the campaign configures.
           * When a user clicks an ad off-platform and lands in the marketplace, the UTM params are stored
           * in the user's Django session and need to be retrieved from the BE.  These values are critical
           * on the page event for ads to be attributed properly.
           *
           * UTM values are only associated to 3 pages currently, so we will check the route to ensure we
           * don't fire this endpoint unnecessarily on every route change.
           */
          const UTM_PAGE_NAMES = ['brand-menu', 'company-dashboard', 'public-menu'];

          const utmParams = UTM_PAGE_NAMES.includes(String(to.name))
            ? await getUtmParams({ clearAfterRetrieve: true }).then((params) => params)
            : {};

          trackPage({
            name: `${pageEventName} Viewed`,
            properties: {
              auth0_id: useAuthStore().userId,
              company_id: activeCompanyId.value,
              hashed_active_company: user.value.hashedActiveCompany,
              is_hijacked: Boolean(user.value.isHijacked),
              marketplace_user_id: user.value.id,
              // trackPage automatically sets the "to" page title and url but it sometimes sets the referrer
              // to the "to" url, which is why we need to ensure referrer is always set to the "from" url
              referrer: from.fullPath,
              split: user.value.splits,
              ...pageMetadata,
              ...utmParams,
            },
          });
        }
      }
    });

    notifyPendingSystemNofications();
  });

  return router;
}

export default createRouter();
